import React, { useState, useEffect, useContext } from 'react'
import I18n from 'i18n'
import TrainingSessionsTable from './TrainingSessionsTable'
import { UpdateTrainingProps } from './TrainingSchedulesCoachView'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from '../../atomic/atoms/Button'
import DeleteButton from '../../atomic/atoms/DeleteButton'
import NewTraining from '../../common/types/NewTraining'
import { newTrainings, setNewTrainings } from './NewTrainingSessions'
import Training from '../../common/types/Training'
import { ModalContext } from '../../modal/ModalProvider'
import DuplicateTrainingModal from './DuplicateTrainingModal'

declare let crypto: any

interface Props {
  schedules: Training[]
  onDelete: (id: number) => void
  onCreateDuplicates?: (duplicates: NewTraining[]) => void
  updateTrainingProps: UpdateTrainingProps<Training>
  toggleUpdate: () => void
  readOnly?: boolean
}

const ScheduledTrainingSessions: React.FC<Props> = ({
  schedules, updateTrainingProps, toggleUpdate, onDelete, onCreateDuplicates, readOnly = false
}) => {
  const [selectedTrainings, setSelectedTrainings] = useState<Training[]>([])
  const { setModalComponent } = useContext(ModalContext)

  // Reset the selection if the contents of the table changes
  useEffect(() => {
    setSelectedTrainings([])
  }, [schedules])

  const onSelectionChanged = (selectedRows: Training[]): void => {
    setSelectedTrainings(selectedRows)
  }

  const copySelectionToNewTrainingSessions = (): void => {
    // create an array of NewTraining objects from the selectedTrainings objects.
    // Make sure they have the correct properties and UUIDs
    // call a function that is given in props to add those to the new training sessions table as new rows
    const newerTrainings = [...newTrainings]
    for (const selectedTraining of selectedTrainings) {
      const newTraining: NewTraining = {
        start_date: selectedTraining.start_date,
        location: selectedTraining.location,
        trainer: selectedTraining.trainer,
        week_type: selectedTraining.week_type,
        training_type: selectedTraining.training_type,
        session_type: selectedTraining.session_type,
        expected_duration: selectedTraining.expected_duration,
        expected_rpe: selectedTraining.expected_rpe,
        athletes: selectedTraining.athletes,
        warmup: selectedTraining.warmup,
        instructions: selectedTraining.instructions ?? '',
        instructions_is_link: selectedTraining.instructions_is_link,
        send_training_log: selectedTraining.send_training_log,
        coach_comments: selectedTraining.coach_comments,
        identifier: crypto.randomUUID()
      }
      newerTrainings.push(newTraining)
    }
    // FORCE UPDATE
    setNewTrainings(newerTrainings, toggleUpdate)
  }

  const deleteSelection = (): void => {
    for (const selectedTraining of selectedTrainings) {
      onDelete(selectedTraining.id)
    }
    setSelectedTrainings([])
  }

  const duplicateSelectionToDates = (dates: Date[]): void => {
    if (onCreateDuplicates === undefined) return
    const duplicates: NewTraining[] = []
    for (const selectedTraining of selectedTrainings) {
      for (const date of dates) {
        duplicates.push({
          start_date: date,
          location: selectedTraining.location,
          trainer: selectedTraining.trainer,
          week_type: selectedTraining.week_type,
          training_type: selectedTraining.training_type,
          session_type: selectedTraining.session_type,
          expected_duration: selectedTraining.expected_duration,
          expected_rpe: selectedTraining.expected_rpe,
          athletes: selectedTraining.athletes,
          warmup: selectedTraining.warmup,
          instructions: selectedTraining.instructions ?? '',
          instructions_is_link: selectedTraining.instructions_is_link,
          send_training_log: selectedTraining.send_training_log,
          coach_comments: selectedTraining.coach_comments,
          identifier: crypto.randomUUID()
        })
      }
    }
    onCreateDuplicates(duplicates)
    setSelectedTrainings([])
  }

  const openDuplicateModal = (): void => {
    setModalComponent(DuplicateTrainingModal, {
      selectedTrainings,
      onConfirm: duplicateSelectionToDates
    })
  }

  return (
    <>
      {selectedTrainings.length > 0 && (
        <div className='row margin-top'>
          <div className='col s12' style={{ display: 'flex' }}>
            <Button autowidth onClick={copySelectionToNewTrainingSessions}>
              <FontAwesomeIcon icon='edit' className='button-icon' />
              {I18n.t('components.trainings.button.copy_to_new_training_sessions')}
            </Button>
            {onCreateDuplicates !== undefined && (
              <Button autowidth onClick={openDuplicateModal}>
                <FontAwesomeIcon icon='copy' className='button-icon' />
                {I18n.t('components.trainings.button.duplicate_to_dates')}
              </Button>
            )}
            <DeleteButton onClick={deleteSelection}>{I18n.t('components.trainings.button.delete')}</DeleteButton>
          </div>
        </div>
      )}
      <div className='row'>
        <div className='col s12'>
          <TrainingSessionsTable
            tableData={schedules}
            updateTrainingProps={updateTrainingProps}
            handleSelectionChanged={onSelectionChanged}
            readOnly={readOnly}
          />
        </div>
      </div>
    </>
  )
}

export default ScheduledTrainingSessions
